// Taki Studio — konsept fotografindaki urunu katalogtaki urunle degistirir.
//
// Tekstil stüdyosundan farki: tasarim yok. Urun tedarikciden HAZIR gelir
// (Rexven SKU'su), konsept fotografi ise kompozisyon sablonudur (manken, poz,
// isik, kadraj). Uretim = sahnedeki takiyi katalogtaki urunle degistirmek.
//
// Bu adimda secim akisi kuruldu; gorsel uretimi bir sonraki adimda baglanacak.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Check, Sparkles, Image as ImageIcon, Info, Plus, ArrowRight, Trash2, Star, X, Maximize2, ExternalLink, Download, ChevronUp, ChevronDown, Wand2 } from "lucide-react";
import { apiClient } from "../lib/api";
import { getBackendUrl, authHeaders } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import { ImageLightbox } from "../components/ImageLightbox";
import { InspirationConceptPicker } from "../components/InspirationConceptPicker";
import { RestyleProductPhotos } from "../components/RestyleProductPhotos";
import { downloadImagesToFolder, folderPickerSupported } from "../lib/downloadToFolder";
import { productJewelryType, JEWELRY_TYPE_LABELS } from "../lib/jewelryType";
import { useJewelryTypes } from "../lib/useJewelryTypes";
import { shotProductKey } from "../lib/useShotProducts";
import type { CustomMockup, ProductCategory, SupplierCatalog } from "@erc/shared";

/** Studio'da uretilen gorseller Listing Builder'a bu anahtarla tasinir.
 *  Zincir Trendyol'daki gibi: Katalog → Studio → Listing Builder. */
export const JEWELRY_SHOTS_KEY = "erc_jewelry_shots";

const abs = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

/** Sunucudaki toplu uretim isi */
type ShotJob = { id: string; status: string; total: number; doneCount: number; failedCount: number };

/** Konsept basina kullanicinin girdigi ek talimat */
type Metal = "" | "gold" | "silver" | "rose" | "all";
type ConceptSlot = { mockupId: string; extraPrompt: string; metal?: Metal; /** Bu sahnede kullanilacak urun fotografi (yoksa urun referansi) */ productRef?: string };

/** Metal rengi talimati (Noel serisi 1. bolum: 3 manken + 3 konsept; konseptlerde
 *  gold, silver ve uc renk bir arada). Bos = urun fotografindaki renk. */
const METAL_LABELS: Record<Exclude<Metal, "">, string> = { gold: "Gold", silver: "Silver", rose: "Rose", all: "3 renk" };
function metalClause(m?: Metal): string {
  switch (m) {
    case "gold": return "Render the product's metal as polished yellow GOLD (14K gold plated look). Change ONLY the metal color; keep the design, stones and engraving identical.";
    case "silver": return "Render the product's metal as polished sterling SILVER. Change ONLY the metal color; keep the design, stones and engraving identical.";
    case "rose": return "Render the product's metal as polished ROSE GOLD. Change ONLY the metal color; keep the design, stones and engraving identical.";
    case "all": return "Show THREE of this exact product side by side in the scene's product spot: one in yellow gold, one in sterling silver, one in rose gold - identical design, neatly arranged, all fully visible.";
    default: return "";
  }
}
const slotPrompt = (slot: ConceptSlot, fallback: string) => [metalClause(slot.metal), slot.extraPrompt || fallback].filter(Boolean).join(" ");

/** Uretilen gorsel + kaynagi: hangi konsept, hangi model ve manken ayari */
type ShotResult = {
  id: string | null;
  imageUrl: string;
  conceptId?: string | null;
  concept?: { id: string; label: string; imageUrl: string; thumbnailUrl?: string | null } | null;
  model?: string | null;
  modelLook?: Record<string, string> | null;
  /** Uretimde kullanilan sahne ve urun referansi */
  sceneUrl?: string | null;
  productRefUrl?: string | null;
  /** Listenin ana gorseli (konsept sirasinda ilk) */
  isPrimary?: boolean;
};
const toShotResult = (x: Record<string, unknown>): ShotResult => ({
  id: (x.id as string) ?? null,
  imageUrl: x.imageUrl as string,
  conceptId: (x.conceptId as string) ?? null,
  concept: (x.concept as ShotResult["concept"]) ?? null,
  model: (x.model as string) ?? null,
  modelLook: (x.modelLook as Record<string, string>) ?? null,
  sceneUrl: (x.sceneUrl as string) ?? null,
  productRefUrl: (x.productRefUrl as string) ?? null,
  isPrimary: x.isPrimary === true,
});
/** Manken ayarini okunur metne cevir (TR etiketlerle) */
function lookText(l?: Record<string, string> | null): string {
  if (!l) return "";
  const lbl = (k: keyof typeof LOOK_OPTIONS, v?: string) => (v ? (LOOK_OPTIONS[k].find(([val]) => val === v)?.[1] ?? v) : "");
  return [
    l.skinTone && `ten: ${lbl("skinTone", l.skinTone)}`,
    (l.hairColor || l.hairTexture) && `saç: ${[lbl("hairColor", l.hairColor), lbl("hairTexture", l.hairTexture)].filter(Boolean).join(", ")}`,
    l.age && `yaş: ${lbl("age", l.age)}`,
    l.nails && `oje: ${lbl("nails", l.nails)}`,
    l.makeup && `makyaj: ${lbl("makeup", l.makeup)}`,
  ].filter(Boolean).join(" · ");
}

/** Sahnede insan var mi (sunucudaki sceneHasPerson ile ayni kural) */
function scenePerson(a?: Record<string, unknown> | null): boolean | null {
  if (!a) return null;
  if (typeof a.hasModel === "boolean") return a.hasModel;
  if (Array.isArray(a.visibleParts)) return (a.visibleParts as unknown[]).length > 0;
  const text = ["pose", "styling", "placement", "summary", "framing"].map((k) => String(a[k] ?? "")).join(" ").toLowerCase();
  if (!text.trim()) return null;
  if (/(manken yok|model yok|insan yok|kişi yok|kisi yok|no model|no person|no human|flat ?lay|still life|natürmort)/i.test(text)) return false;
  return /(manken|model\b|kad[ıi]n|erkek|\bel\b|eller|parmak|kulak|boyun|bilek|dudak|y[üu]z|sa[çc]|ten\b|hand|finger|ear\b|neck|wrist|woman|man\b|lips|face|hair|skin)/i.test(text);
}

type ModelLookState = {
  mode: "keep" | "auto" | "custom";
  skinTone?: string; hairColor?: string; hairTexture?: string; age?: string; nails?: string; makeup?: string;
};
/** Sunucudaki MODEL_LOOK_OPTIONS ile ayni degerler (ingilizce deger, turkce etiket) */
const LOOK_OPTIONS = {
  skinTone: [["fair porcelain", "Çok açık"], ["light beige", "Açık bej"], ["warm olive", "Zeytin"], ["medium tan", "Buğday"], ["golden brown", "Altın esmer"], ["deep brown", "Esmer"], ["rich dark ebony", "Koyu esmer"]],
  hairColor: [["platinum blonde", "Platin sarı"], ["honey blonde", "Bal sarısı"], ["light brown", "Açık kahve"], ["chestnut brown", "Kestane"], ["dark brown", "Koyu kahve"], ["jet black", "Siyah"], ["auburn red", "Kızıl"]],
  hairTexture: [["sleek straight", "Düz"], ["soft waves", "Dalgalı"], ["loose curls", "Bukleli"], ["tight coils", "Kıvırcık"], ["braided", "Örgülü"]],
  age: [["early 20s", "20'lerin başı"], ["late 20s", "20'lerin sonu"], ["30s", "30'lar"], ["40s", "40'lar"], ["50s", "50'ler"]],
  nails: [["bare natural nails", "Ojesiz"], ["nude polish", "Nude"], ["soft pink polish", "Açık pembe"], ["classic red polish", "Kırmızı"], ["french manicure", "French"], ["deep burgundy polish", "Bordo"]],
  makeup: [["no-makeup natural look", "Makyajsız"], ["soft natural makeup", "Doğal"], ["glowy dewy makeup", "Işıltılı"], ["defined elegant makeup", "Belirgin"]],
} as const;

export default function JewelryStudioPage() {
  const { vertical, platform, shop } = useShopVertical();
  // Katalogla AYNI tur listesi (urun tipi profilleri)
  const jewelryTypes = useJewelryTypes();
  const navigate = useNavigate();

  const [catalogs, setCatalogs] = useState<SupplierCatalog[]>([]);
  const [concepts, setConcepts] = useState<CustomMockup[]>([]);
  // Urunun ilham analizlerindeki fotograflardan konsept ekleme penceresi
  const [inspoOpen, setInspoOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  // TR filtresi yalnizca Trendyol icin anlamli; Etsy ABD'ye satis yapar
  const [onlyTR, setOnlyTR] = useState(platform === "trendyol");
  const [productId, setProductId] = useState<string | null>(null);
  // Toplu uretim: kuyruga alinacak ek urunler (secili urun her zaman dahil)
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [job, setJob] = useState<ShotJob | null>(null);
  /** Kuyruktaki urunlerin anahtarlari: bitince Liste Denetimi'ne toplu gonderilir */
  const [jobProducts, setJobProducts] = useState<string[]>([]);
  const [slots, setSlots] = useState<ConceptSlot[]>([]);
  const [promptOnly, setPromptOnly] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");
  /** Klasore indirme ilerlemesi ("3/8") */
  const [downloading, setDownloading] = useState<string | null>(null);
  // Uretilen gorseller — id ile: hatali cikanlar silinebiliyor
  const [results, setResults] = useState<ShotResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Detay paneli acik olan konsept (analiz + referanslar)
  const [openConceptId, setOpenConceptId] = useState<string | null>(null);
  // Uretilen gorsel ve konsept fotograflari tam ekran acilir; oklarla seri
  // gezilir. captions kaynak urun linkini tasir.
  const [lightbox, setLightbox] = useState<{
    urls: string[];
    index: number;
    captions?: Array<{ text?: string | null; url?: string | null }>;
    /** Uretilen gorseller silinebilir; konsept sahneleri buradan silinmez */
    deletable?: boolean;
  } | null>(null);

  // Konsept listesini tam ekran ac — sahneler arasinda gecis yapilabilsin
  const openConceptLightbox = (idx: number) =>
    setLightbox({
      urls: shownConcepts.map((c) => abs(c.imageUrl)),
      index: idx,
      captions: shownConcepts.map((c) => ({
        text: c.label,
        url: (c as unknown as { sourceUrl?: string | null }).sourceUrl ?? null,
      })),
    });
  // Kayitli sahne setleri (konsept koleksiyonlari): ayni secimi her seferinde
  // elle kurmak gerekmesin. Son kullanilan set adi hatirlanir.
  const [sets, setSets] = useState<Array<{ id: string; name: string; mockups: Array<{ id: string }> }>>([]);
  const [activeSetId, setActiveSetId] = useState<string>(() => {
    try { return localStorage.getItem("erc_concept_set") || ""; } catch { return ""; }
  });
  const [savingSet, setSavingSet] = useState(false);
  // Isim/harf kolyesi: gorselde yazacak degerler (katalogtaki orneklerle dolar)
  const [persoValues, setPersoValues] = useState<string[]>([]);
  // Secili urunun turune uyan sahneleri one al (yanlis sahneye para harcanmasin)
  const [matchingOnly, setMatchingOnly] = useState(true);
  // Urun listesi suzgeci: taki turu (addan tahmin edilir)
  const [typeFilter, setTypeFilter] = useState("");
  // Fotograf durumu suzgeci: varsayilan fotografi uretilmemisler (hatirlanir)
  const [shotFilter, setShotFilter] = useState<"todo" | "done" | "all">(() => {
    try { return (localStorage.getItem("erc_studio_shot_filter") as "todo" | "done" | "all") || "todo"; } catch { return "todo"; }
  });
  useEffect(() => { try { localStorage.setItem("erc_studio_shot_filter", shotFilter); } catch { /* yok say */ } }, [shotFilter]);
  // Ilham listesi olanlar suzgeci (hatirlanir)
  const [inspoOnly, setInspoOnly] = useState<boolean>(() => {
    try { return localStorage.getItem("erc_studio_inspo_only") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("erc_studio_inspo_only", inspoOnly ? "1" : "0"); } catch { /* yok say */ } }, [inspoOnly]);
  // Kucuk kare gorselde urun secilemiyor: uzerine gelince buyuk onizleme
  const [preview, setPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  // Uretim modeli: son secim hatirlanir (maliyet/kalite tercihi kalici olsun)
  const [models, setModels] = useState<Array<{ id: string; name: string; description?: string; price?: string }>>([]);
  const [model, setModel] = useState<string>(() => {
    // Varsayilan Sunburst (14/10 karsilastirma: Image 2 isimleri bozuyor, maliyet ayni)
    try { return localStorage.getItem("erc_concept_model_v2") || "openai/gpt-image-2.5-sunburst"; } catch { return "openai/gpt-image-2.5-sunburst"; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cats, conceptList] = await Promise.all([
          apiClient.listSupplierCatalogs(vertical),
          apiClient.listMockups("concept"),
        ]);
        if (cancelled) return;
        setCatalogs(cats ?? []);
        setConcepts(conceptList ?? []);
      } catch { /* ag hatasi — bos liste gosterilir */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [vertical]);

  const loadSets = useCallback(async () => {
    try {
      const rows = await apiClient.listMockupCollections("concept");
      setSets((rows ?? []) as unknown as Array<{ id: string; name: string; mockups: Array<{ id: string }> }>);
    } catch { /* set listesi alinamazsa elle secim calisir */ }
  }, []);
  useEffect(() => { void loadSets(); }, [loadSets]);

  // Son kullanilan set, konseptler yuklendiginde otomatik uygulanir
  const appliedSetRef = useRef(false);
  useEffect(() => {
    if (appliedSetRef.current || !activeSetId || concepts.length === 0 || sets.length === 0) return;
    const hit = sets.find((x) => x.id === activeSetId);
    if (!hit) return;
    appliedSetRef.current = true;
    applySet(hit.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSetId, sets, concepts.length]);

  /** Setteki konseptleri secime al (artik var olmayanlar atlanir) */
  function applySet(id: string) {
    const hit = sets.find((x) => x.id === id);
    if (!hit) return;
    const ids = hit.mockups.map((m) => m.id).filter((mid) => concepts.some((c) => c.id === mid));
    const next = ids.map((mid) => ({ mockupId: mid, extraPrompt: "" }));
    setSlots(next);
    void persistOrder(next);
    setActiveSetId(id);
    try { localStorage.setItem("erc_concept_set", id); } catch { /* ignore */ }
  }

  /** Secili konseptleri yeni set olarak kaydet ya da mevcut seti guncelle */
  async function saveSet() {
    if (slots.length === 0) return;
    const ids = slots.map((x) => x.mockupId);
    const current = sets.find((x) => x.id === activeSetId);
    const suggested = current?.name ?? (productType ? `${productType} sahneleri` : "Sahne seti");
    const name = window.prompt("Set adı", suggested)?.trim();
    if (!name) return;
    setSavingSet(true);
    try {
      if (current && current.name === name) {
        await apiClient.updateMockupCollection(current.id, { mockupIds: ids });
        setLog(`"${name}" güncellendi (${ids.length} konsept)`);
        await loadSets();
      } else {
        const created = await apiClient.createMockupCollection(name, ids, null, "concept");
        setActiveSetId(created.id);
        try { localStorage.setItem("erc_concept_set", created.id); } catch { /* ignore */ }
        setLog(`"${name}" kaydedildi (${ids.length} konsept)`);
        await loadSets();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Set kaydedilemedi");
    } finally { setSavingSet(false); }
  }

  // Gorsel uretim modelleri (panelin ortak listesi)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${getBackendUrl()}/api/analyses/design/models`, { headers: authHeaders() });
        const b = await r.json().catch(() => ({}));
        if (cancelled || !r.ok) return;
        const list = (b.data ?? []) as Array<{ id: string; name: string; description?: string; price?: string }>;
        setModels(list);
        // Kayitli secim listede yoksa ilkine dus
        setModel((cur) => (cur && list.some((m) => m.id === cur) ? cur : list[0]?.id ?? ""));
      } catch { /* liste alinamazsa sunucu varsayilani kullanilir */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { if (model) localStorage.setItem("erc_concept_model_v2", model); } catch { /* ignore */ }
  }, [model]);

  // Platform degisince TR filtresi de degisir (Trendyol'da acik, Etsy'de kapali)
  useEffect(() => { setOnlyTR(platform === "trendyol"); }, [platform]);

  // Katalog urunlerini tek listeye ac; TR filtresi Trendyol'da varsayilan acik
  // cunku orada yalnizca TR'ye uygun urunler satilabiliyor.
  const products = useMemo(() => {
    const rows: Array<{ catalog: SupplierCatalog; cat: ProductCategory }> = [];
    for (const c of catalogs) {
      for (const cat of (c.categories ?? []) as ProductCategory[]) {
        if (cat.discontinued) continue; // tedarikci kaldirdi
        const markets = cat.source?.markets;
        // Pazar bilgisi GIRILMEMIS urunler filtrede gizlenmez — eski
        // aktarimlar aniden kaybolmasin (katalog sayfasiyla ayni kural).
        if (onlyTR && markets?.length && !markets.includes("TR")) continue;
        rows.push({ catalog: c, cat });
      }
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(({ cat }) =>
      cat.name.toLowerCase().includes(needle) ||
      (cat.source?.sku ?? "").toLowerCase().includes(needle) ||
      (cat.brand ?? "").toLowerCase().includes(needle));
  }, [catalogs, onlyTR, q]);

  // Urun basina hazir gorsel sayisi; urun degisince tazelenir (yeni uretilenler dussun)
  const { data: shotCounts = {}, refetch: refetchShotCounts } = useQuery({
    queryKey: ["product-shot-counts"],
    queryFn: async (): Promise<Record<string, number>> => {
      const r = await fetch(`${getBackendUrl()}/api/product-shots/counts`, { headers: authHeaders() });
      return r.ok ? (await r.json()).data ?? {} : {};
    },
  });
  useEffect(() => { void refetchShotCounts(); }, [productId, refetchShotCounts]);
  const hasShots = (id: string) => (shotCounts[id] ?? 0) > 0;

  // Tur ve fotograf suzgecleri AYRI katman: etiketlerdeki sayilar diger suzgecten
  // etkilenmemeli, yoksa bir secim yapilinca digerleri sifir gorunup kayboluyor.
  // Secili urun suzgece takilsa da listede kalir (uretim bitince kaybolmasin).
  const shownProducts = useMemo(() => products.filter(({ cat }) => {
    if (cat.id === productId) return true;
    if (shotFilter === "todo" && hasShots(cat.id)) return false;
    if (shotFilter === "done" && !hasShots(cat.id)) return false;
    if (inspoOnly && !(cat.inspirations?.length)) return false;
    if (!typeFilter) return true;
    return typeFilter === "__none__" ? productJewelryType(cat) === null : productJewelryType(cat) === typeFilter;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [products, typeFilter, shotFilter, shotCounts, productId, inspoOnly]);

  const selected = products.find(({ cat }) => cat.id === productId);
  // Urun degisince URET listesi (secili konseptler, ek talimatlar) sifirlanir;
  // onceki urunun secimleri yeni urune tasinmasin. Ilk acilista dokunulmaz.
  const prevProductRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // Ilk urun secimi (bos -> urun) sifirlamaz: son kullanilan set korunur
    if (prevProductRef.current === undefined || prevProductRef.current === null) { prevProductRef.current = productId; return; }
    if (prevProductRef.current === productId) return;
    prevProductRef.current = productId;
    setSlots([]);
    setActiveSetId("");
    setLog("");
    setError(null);
  }, [productId]);
  // Manken gorunumu: referans sahneyle birebir ayni kisi cikmasin
  const [look, setLook] = useState<ModelLookState>(() => {
    try { return JSON.parse(localStorage.getItem("erc:jewelry-model-look") || "") as ModelLookState; } catch { return { mode: "keep" }; }
  });
  useEffect(() => { try { localStorage.setItem("erc:jewelry-model-look", JSON.stringify(look)); } catch { /* yok */ } }, [look]);
  const lookPayload = look.mode === "keep" ? undefined : look;
  const productType = productJewelryType(selected?.cat);

  // ── Uretim ayarlari: TURE GORE hatirlanir + adlandirilmis kayitlar ──
  // Hesapta (UserSetting "jewelry_shot_prefs") saklanir; cihazlar arasi gecerli.
  type ShotSettings = { look: ModelLookState; model: string; extraPrompt: string };
  type ShotPrefs = { byType: Record<string, ShotSettings>; presets: Array<{ id: string; name: string; s: ShotSettings }> };
  const [prefs, setPrefs] = useState<ShotPrefs>({ byType: {}, presets: [] });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [presetId, setPresetId] = useState("");
  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch(`${getBackendUrl()}/api/settings`, { headers: authHeaders() });
        const j = await r.json().catch(() => ({}));
        const raw = j.data?.jewelry_shot_prefs;
        if (raw) setPrefs({ byType: {}, presets: [], ...(JSON.parse(raw) as Partial<ShotPrefs>) });
      } catch { /* ayarlar alinamazsa varsayilanlarla devam */ }
      setPrefsLoaded(true);
    })();
  }, []);
  const persistPrefs = (next: ShotPrefs) => {
    setPrefs(next);
    void fetch(`${getBackendUrl()}/api/settings`, {
      method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ jewelry_shot_prefs: JSON.stringify(next) }),
    }).catch(() => { /* bir sonraki degisiklikte tekrar denenir */ });
  };
  const currentSettings = (): ShotSettings => ({ look, model, extraPrompt: promptOnly });
  const applySettings = (x: ShotSettings) => {
    setLook(x.look ?? { mode: "keep" });
    if (x.model) setModel(x.model);
    setPromptOnly(x.extraPrompt ?? "");
  };
  // Tur degisince o turun son ayarlari yuklenir
  const lastTypeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!prefsLoaded || !productType || lastTypeRef.current === productType) return;
    lastTypeRef.current = productType;
    const saved = prefs.byType[productType];
    if (saved) applySettings(saved);
    setPresetId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, prefsLoaded]);
  // Degisiklikler o ture otomatik yazilir (kisa gecikmeyle)
  useEffect(() => {
    if (!prefsLoaded || !productType || lastTypeRef.current !== productType) return;
    const cur = currentSettings();
    if (JSON.stringify(prefs.byType[productType] ?? null) === JSON.stringify(cur)) return;
    const t = setTimeout(() => persistPrefs({ ...prefs, byType: { ...prefs.byType, [productType]: cur } }), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look, model, promptOnly, productType, prefsLoaded]);
  const perso = (selected?.cat as unknown as { personalization?: { enabled: boolean; slots: number; samples: string[]; font?: string; note?: string } } | undefined)?.personalization;

  // Urun degisince kisisellestirme alanlari katalogtaki orneklerle dolar
  useEffect(() => {
    if (!perso?.enabled) { setPersoValues([]); return; }
    setPersoValues(Array.from({ length: perso.slots }, (_, i) => perso.samples?.[i] ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.cat.id, perso?.enabled, perso?.slots]);

  /** Uretim prompt'una eklenecek kisisellestirme talimati */
  function personalizationClause(): string {
    if (!perso?.enabled) return "";
    const values = persoValues.map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) return "";
    const names = values.map((v) => `"${v}"`).join(" and ");
    const font = perso.font?.trim();
    return [
      `The jewellery is personalised and must clearly show ${values.length > 1 ? "the names" : "the name"} ${names}.`,
      "Spell them EXACTLY as written, correct letter by letter, no extra or missing characters.",
      font ? `Lettering style: ${font}.` : "Lettering must match the style of the product photo.",
      "Keep the text sharp, legible and correctly proportioned on the piece.",
      perso.note?.trim() ? `Note: ${perso.note.trim()}` : "",
    ].filter(Boolean).join(" ");
  }
  // Uyumlu sahneler once; "yalnizca uyumlu" acikken digerleri gizlenir.
  // Konseptin tipi girilmemisse gizlenmez: eski kayitlar kaybolmasin.
  const shownConcepts = (() => {
    if (!productType) return concepts;
    // Kesin eslesme: kolye urununde yalnizca kolye sahneleri. Tipi girilmemis
    // konseptler de gizlenir — yanlis sahneye uretim hem kotu sonuc verir hem
    // ucretli. Tipi olmayanlari gormek icin anahtari kapatmak yeterli.
    const fits = (c: CustomMockup) => c.productTypeKey === productType;
    const list = matchingOnly ? concepts.filter(fits) : concepts;
    return [...list].sort((a, b) => Number(b.productTypeKey === productType) - Number(a.productTypeKey === productType));
  })();
  // Urun referansi: katalogda secilen (sade urun fotografi) ya da ilk fotograf
  const [refOverride, setRefOverride] = useState<Record<string, string>>({});
  const productImage = selected
    ? (refOverride[selected.cat.id] ?? selected.cat.referenceImageUrl ?? selected.cat.source?.images?.[0])
    : undefined;
  const chooseReference = async (url: string) => {
    if (!selected) return;
    setRefOverride((m) => ({ ...m, [selected.cat.id]: url }));
    try {
      await fetch(`${getBackendUrl()}/api/supplier-catalogs/${selected.catalog.id}/categories/${selected.cat.id}/reference-image`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ url }),
      });
    } catch { /* kaydedilemezse yalniz bu oturumda gecerli */ }
  };

  /** Urunun galerisini sunucudan al (ana gorsel + konsept sirasiyla) */
  async function fetchResults(catalogId: string, categoryId: string): Promise<ShotResult[] | null> {
    try {
      const r = await fetch(
        `${getBackendUrl()}/api/product-shots?catalogId=${catalogId}&categoryId=${categoryId}`,
        { headers: authHeaders() },
      );
      const b = await r.json().catch(() => ({}));
      return r.ok ? ((b.data ?? []) as Array<Record<string, unknown>>).map(toShotResult) : null;
    } catch { return null; }
  }

  // Urunun DAHA ONCE uretilmis gorselleri — ayni sahneyi ikinci kez uretme
  useEffect(() => {
    if (!selected) { setResults([]); return; }
    let cancelled = false;
    void fetchResults(selected.catalog.id, selected.cat.id).then((rows) => {
      if (!cancelled && rows) setResults(rows);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.catalog.id, selected?.cat.id]);

  /** Konsept sirasini urunun gorsellerine uygula: liste bu siraya dizilir,
   *  ilk konseptin gorseli ana gorsel olur. Sunucuya yazilir; Liste Denetimi
   *  ve Listing Builder ayni sirayi gorur. */
  async function persistOrder(order: ConceptSlot[]) {
    if (!selected || order.length === 0) return;
    const { catalog, cat } = selected;
    try {
      const r = await fetch(`${getBackendUrl()}/api/product-shots/apply-concept-order`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ catalogId: catalog.id, categoryId: cat.id, conceptIds: order.map((x) => x.mockupId) }),
      });
      if (!r.ok) return;
      const rows = await fetchResults(catalog.id, cat.id);
      if (rows) setResults(rows);
    } catch { /* siralanamazsa mevcut sira kalir */ }
  }

  /** Secili konseptin sirasini degistir (dir: -1 yukari, +1 asagi) */
  function moveSlot(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= slots.length) return;
    const next = [...slots];
    [next[i], next[j]] = [next[j], next[i]];
    setSlots(next);
    void persistOrder(next);
  }

  /** Konsepti ANA gorsel yap: sira 1'e alinir (Etsy'de ana gorsel ilk gorseldir) */
  function makeMain(i: number) {
    if (i <= 0) return;
    const next = [slots[i], ...slots.filter((_, k) => k !== i)];
    setSlots(next);
    void persistOrder(next);
  }


  // Uretilen kareyi konsept kutuphanesine ekle (baska urunlerde sahne olarak)
  const [conceptSaving, setConceptSaving] = useState<string | null>(null);
  const [savedAsConcept, setSavedAsConcept] = useState<Set<string>>(new Set());
  async function addShotAsConcept(imageUrl: string) {
    setConceptSaving(imageUrl); setError(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/from-url`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ url: imageUrl, kind: "concept", label: `Üretilen: ${selected?.cat.name ?? "kare"}`.slice(0, 120), productTypeKey: productType || undefined }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Konseptlere eklenemedi");
      setConcepts((prev) => [b.data as CustomMockup, ...prev]);
      setSavedAsConcept((s) => new Set(s).add(imageUrl));
      setLog("Kare konsept kütüphanesine eklendi.");
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setConceptSaving(null); }
  }

  /** Secili konseptlerde urunu gosteren gorselleri sirayla uretir. */
  async function generate() {
    if (!productImage || slots.length === 0) return;
    setBusy(true); setError(null);
    let done = 0;
    for (const slot of slots) {
      const c = concepts.find((x) => x.id === slot.mockupId);
      if (!c) continue;
      setLog(`${done + 1}/${slots.length} — ${c.label || "konsept"} üretiliyor…`);
      try {
        const r = await fetch(`${getBackendUrl()}/api/media/generate-concept-shot`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            conceptImageUrl: c.imageUrl,
            productImageUrl: slot.productRef || productImage,
            extraPrompt: [personalizationClause(), slotPrompt(slot, promptOnly)].filter(Boolean).join(" ") || undefined,
            productHint: selected?.cat.name,
            // Uretilen gorsel urunun galerisine yazilsin (kalici olsun)
            conceptId: c.id,
            catalogId: selected?.catalog.id,
            categoryId: selected?.cat.id,
            model: model || undefined,
            modelLook: lookPayload,
          }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || "Üretilemedi");
        if (body.data?.imageUrl) {
          setResults((prev) => [...prev, {
            id: (body.data.shotId as string) ?? null,
            imageUrl: body.data.imageUrl as string,
            conceptId: c.id,
            concept: { id: c.id, label: c.label, imageUrl: c.imageUrl, thumbnailUrl: c.thumbnailUrl },
            model: model || null,
            modelLook: (body.data.modelLook as Record<string, string>) ?? null,
            sceneUrl: c.imageUrl,
            productRefUrl: slot.productRef || productImage,
          }]);
        }
        done++;
      } catch (e) {
        setError(`${c.label || "Konsept"}: ${e instanceof Error ? e.message : "üretilemedi"}`);
      }
    }
    setLog(done ? `${done} görsel üretildi` : "");
    // Uretilenler konsept sirasina dizilir (ilk konsept = ana gorsel)
    if (done) await persistOrder(slots);
    setBusy(false);
  }

  /** Uretilen gorsellerle Listing Builder'i ac. Gorseller sessionStorage
   *  uzerinden tasinir; builder acilista bunlari liste gorsellerine ekler. */
  function toListingBuilder() {
    if (!selected) return;
    const { catalog, cat } = selected;
    try {
      sessionStorage.setItem(JEWELRY_SHOTS_KEY, JSON.stringify({ categoryId: cat.id, images: results.map((r) => r.imageUrl) }));
    } catch { /* sessionStorage kapaliysa gorseller builder'da yeniden uretilir */ }
    navigate(`/listing-builder/jewelry?catalogId=${catalog.id}&categoryId=${cat.id}`);
  }

  /** Secili urunleri (ve varsa toplu secimi) konseptlerle kuyruga al.
   *  Uretim sunucuda sirayla isler; panel kapansa da devam eder. */
  async function enqueueBatch() {
    if (!selected || slots.length === 0) return;
    const categoryIds = Array.from(new Set([selected.cat.id, ...batchIds]));
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/shot-jobs`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          catalogId: selected.catalog.id,
          categoryIds,
          conceptIds: slots.map((x) => x.mockupId),
          extraPrompt: [personalizationClause(), promptOnly].filter(Boolean).join(" ") || undefined,
          // Sahneye ozel talimat + metal rengi (kuyruk genel talimata ekler)
          conceptPrompts: Object.fromEntries(slots.map((x) => [x.mockupId, slotPrompt(x, "")]).filter(([, v]) => v)),
          model: model || undefined,
          modelLook: lookPayload,
        }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Kuyruğa alınamadı");
      setJob(b.data as ShotJob);
      setJobProducts(categoryIds.map((id) => shotProductKey(selected.catalog.id, id)));
      setLog(`${categoryIds.length} ürün x ${slots.length} konsept kuyruğa alındı`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kuyruğa alınamadı");
    } finally { setBusy(false); }
  }

  /** Aktif isi periyodik izle; bitince galeriyi tazele */
  useEffect(() => {
    if (!job || ["done", "error", "cancelled"].includes(job.status)) return;
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${getBackendUrl()}/api/shot-jobs/${job.id}`, { headers: authHeaders() });
        const b = await r.json().catch(() => ({}));
        if (!r.ok) return;
        const next = b.data as ShotJob;
        setJob(next);
        if (["done", "error", "cancelled"].includes(next.status)) {
          clearInterval(t);
          // Uretilen gorseller galeriye yazildi: listeyi tazele
          // (sunucu is bitince konsept sirasini da uygular)
          if (selected) {
            const rows = await fetchResults(selected.catalog.id, selected.cat.id);
            if (rows) setResults(rows);
          }
        }
      } catch { /* ag hatasi — sonraki turda tekrar denenir */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  /** Hatali uretilen gorseli galeriden sil. Uretim ucuz degil ama kotu
   *  cikan kareyi listede tutmak daha pahali: liste gorseli olarak secilme
   *  riski var. */
  async function deleteShot(i: number) {
    const shot = results[i];
    if (!shot) return;
    if (!window.confirm("Bu görsel silinsin mi?")) return;
    try {
      if (shot.id) {
        const r = await fetch(`${getBackendUrl()}/api/product-shots/${shot.id}`, {
          method: "DELETE", headers: authHeaders(),
        });
        if (!r.ok) throw new Error("Silinemedi");
      }
      const next = results.filter((_, idx) => idx !== i);
      setResults(next);
      // Tam ekran aciksa: liste bitince kapat, degilse siradaki karede kal
      setLightbox((lb) => {
        if (!lb) return lb;
        if (next.length === 0) return null;
        return { urls: next.map((x) => abs(x.imageUrl)), index: Math.min(i, next.length - 1), deletable: true };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Görsel silinemedi");
    }
  }

  /** Uretilen bir sonucu konseptin onayli ornegi olarak kaydet */
  async function addSampleOutput(conceptId: string, imageUrl: string) {
    try {
      await fetch(`${getBackendUrl()}/api/mockups/${conceptId}/references`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ kind: "sample_output", imageUrl }),
      });
      setLog("Örnek çıktı konsepte eklendi");
    } catch { setError("Örnek eklenemedi"); }
  }

  /** Standart 6'li set (Noel serisi 1. bolum): urun tipine uyan sahnelerden 3
   *  mankenli (renk aynen / gold / silver) + 3 urun sahnesi (gold / silver /
   *  uc renk). Yeterli sahne yoksa bulunanla kurulur. */
  function applyStandardSet() {
    const pool = shownConcepts;
    const withModel = pool.filter((c) => scenePerson(c.conceptAnalysis as Record<string, unknown> | null) === true).slice(0, 3);
    const still = pool.filter((c) => scenePerson(c.conceptAnalysis as Record<string, unknown> | null) === false).slice(0, 3);
    const modelMetals: Metal[] = ["", "gold", "silver"];
    const stillMetals: Metal[] = ["gold", "silver", "all"];
    const next: ConceptSlot[] = [
      ...withModel.map((c, i) => ({ mockupId: c.id, extraPrompt: "", metal: modelMetals[i] })),
      ...still.map((c, i) => ({ mockupId: c.id, extraPrompt: "", metal: stillMetals[i] })),
    ];
    if (!next.length) { setError("Bu ürün tipine uygun, analizli konsept yok — Konsept Fotoğrafları'ndan sahneleri analiz et."); return; }
    setSlots(next);
    setActiveSetId("");
    const miss = [withModel.length < 3 ? `${3 - withModel.length} mankenli` : "", still.length < 3 ? `${3 - still.length} ürün sahnesi` : ""].filter(Boolean).join(" ve ");
    setLog(miss ? `Set kuruldu (${next.length} sahne) — eksik: ${miss}` : "Standart 6'lı set kuruldu");
  }

  /** Uretilmis kareye tek cumlelik duzeltme: yeni kare galeriye eklenir */
  const [revising, setRevising] = useState<string | null>(null);
  async function reviseShot(i: number) {
    const shot = results[i];
    if (!shot?.id) return;
    const instruction = window.prompt("Bu görselde neyi değiştirelim? Tek net cümle yaz (ör. \"mankeni siyahi yap\", \"arka planı bej yap\")");
    if (!instruction?.trim()) return;
    setRevising(shot.id); setError(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/media/revise-shot`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ shotId: shot.id, instruction, productImageUrl: productImage, model: model || undefined }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Düzeltilemedi");
      const next = [...results];
      next.splice(i + 1, 0, { ...shot, id: b.data.shotId as string, imageUrl: b.data.imageUrl as string, isPrimary: false });
      setResults(next);
      setLog("Düzeltilmiş görsel eklendi — eskisini istersen sil");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Görsel düzeltilemedi");
    } finally { setRevising(null); }
  }

  function toggleConcept(id: string) {
    setSlots((prev) => {
      const hit = prev.findIndex((s) => s.mockupId === id);
      if (hit >= 0) return prev.filter((_, i) => i !== hit);
      // Ust sinir simdilik yok: kac sahne secilecegi kullaniciya birakildi
      // (uretim kuyrukta sirayla islendigi icin sayi teknik bir engel degil).
      return [...prev, { mockupId: id, extraPrompt: "" }];
    });
  }

  if (platform !== "trendyol" && vertical !== "jewelry") {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h1 className="text-sm font-semibold text-amber-900">Takı mağazası seçili değil</h1>
          <p className="mt-1 text-sm text-amber-800">
            Bu ekran takı üretimi içindir. Tekstil üretimi için Mockup Studio'yu kullanın.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1800px] p-6">
      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <Sparkles className="h-5 w-5 text-pink-600" /> Studio
          <Link to="/jewelry-studio/bulk" className="ml-2 rounded-full border border-gray-300 px-3 py-0.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Toplu üretim →</Link>
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Konsept fotoğrafındaki ürünü katalogdaki ürünle değiştir. {shop?.shopName ? `Mağaza: ${shop.shopName}` : ""}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(340px,1fr)_minmax(460px,1.6fr)_minmax(320px,0.9fr)]">
          {/* 1. Urun */}
          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-900">1 · Ürün seç</h2>
            <div className="mb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ürün adı veya SKU"
                  className="w-full rounded-lg border border-gray-200 py-1.5 pl-7 pr-2 text-sm"
                />
              </div>
              <label className="flex items-center gap-1 text-[11px] text-gray-600">
                <input type="checkbox" checked={onlyTR} onChange={(e) => setOnlyTR(e.target.checked)} />
                TR
              </label>
            </div>

            {/* Fotograf durumu */}
            <div className="mb-2 flex gap-1 rounded-lg bg-gray-100 p-0.5 text-xs" role="group" aria-label="Fotoğraf durumu">
              {([["todo", "Fotoğrafı yok"], ["done", "Fotoğraflı"], ["all", "Tümü"]] as const).map(([val, label]) => {
                const n = val === "all" ? products.length : products.filter(({ cat }) => hasShots(cat.id) === (val === "done")).length;
                return (
                  <button key={val} onClick={() => setShotFilter(val)} aria-pressed={shotFilter === val}
                    className={`flex-1 rounded-md px-2 py-1 ${shotFilter === val ? "bg-white font-medium text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    {label} <span className="text-gray-400">{n}</span>
                  </button>
                );
              })}
            </div>

            <label className="mb-2 flex items-center gap-1.5 text-xs text-gray-700">
              <input type="checkbox" checked={inspoOnly} onChange={(e) => setInspoOnly(e.target.checked)} className="accent-indigo-600" />
              Yalnız ilham listesi olanlar
              <span className="text-gray-500">({products.filter(({ cat }) => (cat.inspirations?.length ?? 0) > 0).length})</span>
            </label>

            {/* Kategori suzgeci: tur katalogta ELLE secildiyse o gecerli,
                secilmemisse urun adindan tahmin edilir. */}
            <div className="mb-2 flex flex-wrap gap-1">
              {[["", "Tümü"], ...jewelryTypes.map((t) => [t.key, t.label] as const), ["__none__", "tipi bilinmeyen"]].map(([val, label]) => {
                const count = val === ""
                  ? products.length
                  : products.filter(({ cat }) => (val === "__none__"
                      ? productJewelryType(cat) === null
                      : productJewelryType(cat) === val)).length;
                const on = typeFilter === val;
                if (val !== "" && count === 0 && !on) return null;
                return (
                  <button
                    key={val || "all"}
                    onClick={() => setTypeFilter(val as string)}
                    className={`rounded-full border px-2.5 py-1 text-xs ${
                      on ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {label}{val === typeFilter || val === "" ? "" : ` (${count})`}
                  </button>
                );
              })}
            </div>

            {shownProducts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center">
                <p className="text-xs text-gray-500">
                  {products.length === 0 ? "Bu dikeyde katalog ürünü yok." : shotFilter === "todo" ? "Bu süzgeçte fotoğrafı üretilmemiş ürün kalmadı." : "Bu süzgece uyan ürün yok."}
                </p>
                {products.length > 0 && shotFilter !== "all" && (
                  <button onClick={() => setShotFilter("all")} className="mt-2 block w-full text-xs text-indigo-600 hover:underline">Tümünü göster</button>
                )}
                <Link to="/supplier-catalogs" className="mt-2 inline-block text-xs text-indigo-600 hover:underline">
                  Tedarikçi Katalog'a git
                </Link>
              </div>
            ) : (
              <div className="max-h-[620px] space-y-1 overflow-y-auto">
                {shownProducts.map(({ cat }) => {
                  const on = cat.id === productId;
                  const img = cat.source?.images?.[0];
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setProductId(on ? null : cat.id)}
                      className={`flex w-full items-start gap-2 rounded-lg border p-2 text-left ${
                        on ? "border-indigo-400 bg-indigo-50" : "border-gray-100 hover:border-gray-300"
                      }`}
                    >
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          loading="lazy"
                          onMouseEnter={(e) => setPreview({ url: img, x: e.clientX, y: e.clientY })}
                          onMouseMove={(e) => setPreview({ url: img, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setPreview(null)}
                          className="h-24 w-24 shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                          <ImageIcon className="h-5 w-5 text-gray-300" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium leading-snug text-gray-800">{cat.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {cat.brand}{cat.source?.sku ? ` · ${cat.source.sku}` : ""}
                        </span>
                        {hasShots(cat.id) && (
                          <span className="mt-1 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{shotCounts[cat.id]} görsel</span>
                        )}
                      </span>
                      {/* Kisayollar: urunun Rexven sayfasi ve katalogtaki karti */}
                      <span className="flex shrink-0 flex-col gap-1">
                        {cat.source?.url && (
                          <a
                            href={cat.source.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Rexven'de aç"
                            className="rounded border border-gray-200 px-1 py-0.5 text-[9px] text-gray-500 hover:border-orange-300 hover:text-orange-600"
                          >
                            Rexven
                          </a>
                        )}
                        <Link
                          to="/supplier-catalogs"
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Katalogda aç"
                          className="rounded border border-gray-200 px-1 py-0.5 text-[9px] text-gray-500 hover:border-indigo-300 hover:text-indigo-600"
                        >
                          Katalog
                        </Link>
                      </span>
                      {on && <Check className="h-4 w-4 shrink-0 text-indigo-600" />}
                      {/* Toplu uretim: bu urunu de kuyruga kat */}
                      <span
                        role="checkbox"
                        aria-checked={batchIds.includes(cat.id)}
                        tabIndex={0}
                        title="Toplu üretime ekle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBatchIds((prev) => prev.includes(cat.id) ? prev.filter((x) => x !== cat.id) : [...prev, cat.id]);
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setBatchIds((prev) => prev.includes(cat.id) ? prev.filter((x) => x !== cat.id) : [...prev, cat.id]); } }}
                        className={`ml-1 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] ${
                          batchIds.includes(cat.id) ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300 text-transparent"
                        }`}
                      >
                        +
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* 2. Konsept */}
          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">2 · Konsept seç ({slots.length} seçili)</h2>
              {selected && (
                <button onClick={() => setInspoOpen(true)} title="Ürünün ilham analizlerindeki fotoğrafları konsept olarak ekle"
                  className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <ImageIcon className="h-3.5 w-3.5" /> İlham fotoğrafları
                </button>
              )}
              {inspoOpen && selected && (
                <InspirationConceptPicker cat={selected.cat} productTypeKey={productType} onClose={() => setInspoOpen(false)}
                  library={concepts}
                  picked={slots.map((x) => ({ conceptId: x.mockupId, productRef: x.productRef || productImage || "" }))}
                  onAdded={(m, productRef) => {
                    setConcepts((prev) => [m, ...prev.filter((c) => c.id !== m.id)]);
                    // Ayni sahne baska urun fotografina alinirsa eslesmesi guncellenir
                    setSlots((prev) => (prev.some((x) => x.mockupId === m.id)
                      ? prev.map((x) => (x.mockupId === m.id ? { ...x, productRef } : x))
                      : [...prev, { mockupId: m.id, extraPrompt: "", productRef }]));
                  }}
                  onRemoved={(id) => setSlots((prev) => prev.filter((x) => x.mockupId !== id))} />
              )}
              {productType && (
                <label className="flex items-center gap-1 text-[10px] text-gray-600" title={`Seçili ürün: ${productType}`}>
                  <input type="checkbox" checked={matchingOnly} onChange={(e) => setMatchingOnly(e.target.checked)} />
                  yalnızca {productType}
                </label>
              )}
            </div>
            <p className="mb-2 text-[11px] text-gray-500">
              Konsept = kompozisyon şablonu (manken, poz, ışık). Ürün bu sahneye yerleştirilir.
            </p>

            {/* Kayitli sahne setleri: ayni secim tekrar kurulabilsin */}
            <div className="mb-2 flex items-center gap-1.5">
              <select
                value={activeSetId}
                onChange={(e) => (e.target.value ? applySet(e.target.value) : setActiveSetId(""))}
                className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]"
              >
                <option value="">Kayıtlı set seç…</option>
                {sets.map((x) => (
                  <option key={x.id} value={x.id}>{x.name} ({x.mockups.length})</option>
                ))}
              </select>
              <button
                onClick={saveSet}
                disabled={savingSet || slots.length === 0}
                title="Seçili konseptleri set olarak kaydet"
                className="rounded border border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
              >
                {savingSet ? "…" : "Seçimi kaydet"}
              </button>
              <button
                onClick={applyStandardSet}
                title="3 mankenli (aynen / gold / silver) + 3 ürün sahnesi (gold / silver / 3 renk)"
                className="rounded border border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
              >
                Standart 6'lı set
              </button>
              {slots.length > 0 && (
                <button onClick={() => setSlots([])} className="text-[11px] text-gray-500 hover:text-gray-700">
                  Temizle
                </button>
              )}
            </div>

            {matchingOnly && productType && shownConcepts.length === 0 && concepts.length > 0 ? (
              <p className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                "{productType}" tipinde konsept yok. Konsept Fotoğrafları ekranından sahnelere ürün tipi
                atayabilir ya da yukarıdaki anahtarı kapatıp tüm sahneleri görebilirsin.
              </p>
            ) : concepts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center">
                <p className="text-xs text-gray-500">Henüz konsept fotoğrafı yok.</p>
                <Link to="/mockup-manager" className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                  <Plus className="h-3 w-3" /> Konsept Fotoğrafları
                </Link>
              </div>
            ) : (
              <div className="grid max-h-[620px] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                {shownConcepts.map((c, ci) => {
                  const order = slots.findIndex((s) => s.mockupId === c.id);
                  const on = order >= 0;
                  // Secili urun icin bu konseptten uretilmis gorsel sayisi
                  const made = results.filter((r) => (r.conceptId ?? r.concept?.id) === c.id).length;
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleConcept(c.id)}
                      className={`relative overflow-hidden rounded-lg border-2 ${on ? "border-pink-500" : "border-transparent hover:border-gray-300"}`}
                    >
                      <img src={abs(c.thumbnailUrl || c.imageUrl)} alt={c.label} loading="lazy" className="h-40 w-full object-cover" />
                      {on && (
                        <span title={order === 0 ? "Sıra 1 — ana görsel" : `Sıra ${order + 1}`}
                          className={`absolute right-1 top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${order === 0 ? "bg-amber-500" : "bg-pink-600"}`}>
                          {order === 0 ? <Star className="h-3 w-3 fill-white" /> : order + 1}
                        </span>
                      )}
                      {made > 0 && (
                        <span title={`Bu ürün için bu konseptten ${made} görsel üretildi`}
                          className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-emerald-600/90 px-1 py-0.5 text-[9px] font-semibold text-white">
                          <Check className="h-2.5 w-2.5" /> {made} üretildi
                        </span>
                      )}
                      {/* Konseptin cozumlenmis urun tipi — dogru sahneyi secmek icin */}
                      {c.productTypeKey && (
                        <span className="absolute bottom-0 left-0 bg-black/55 px-1 text-[9px] text-white">{c.productTypeKey}</span>
                      )}
                      {/* Tam ekran: sahneyi buyuk gormek secim kararini kolaylastirir.
                          Karta tiklamak SECIYOR, buyutec ise onizliyor. */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); openConceptLightbox(ci); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openConceptLightbox(ci); } }}
                        title="Tam ekran aç (oklarla gezinir)"
                        className="absolute bottom-0.5 right-6 rounded bg-white/85 p-0.5 text-gray-600 hover:bg-white"
                      >
                        <Maximize2 className="h-3 w-3" />
                      </span>
                      {/* Detay: analiz + referanslar */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setOpenConceptId(c.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setOpenConceptId(c.id); } }}
                        title="Konsept analizi ve referanslar"
                        className="absolute bottom-0.5 right-0.5 rounded bg-white/85 p-0.5 text-gray-600 hover:bg-white"
                      >
                        <Info className="h-3 w-3" />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* 3. Uretim */}
          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-900">3 · Üret</h2>

            {selected ? (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                {productImage
                  ? <img src={productImage} alt="" className="h-12 w-12 rounded object-cover" />
                  : <span className="flex h-12 w-12 items-center justify-center rounded bg-gray-100"><ImageIcon className="h-4 w-4 text-gray-300" /></span>}
                <div className="min-w-0">
                  <p className="text-xs font-medium leading-snug text-gray-800">{selected.cat.name}</p>
                  <p className="truncate text-[10px] text-gray-400">{selected.cat.source?.sku}</p>
                </div>
              </div>
            ) : (
              <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">Önce bir ürün seçin.</p>
            )}
            {selected && (selected.cat.source?.images?.length ?? 0) > 1 ? (
              <div className="mb-3">
                <p className="mb-1 text-[11px] text-gray-500">
                  Ürün referansı — modele verilecek ürün fotoğrafı. <b>Sade/yakın ürün fotoğrafı</b> seçin; elde tutulan ya da yazılı kareler kompozisyonu bozar:
                </p>
                <div className="flex flex-wrap gap-1">
                  {selected.cat.source!.images!.slice(0, 12).map((u) => (
                    <button key={u} onClick={() => void chooseReference(u)}
                      className={`h-12 w-12 overflow-hidden rounded border-2 ${u === productImage ? "border-pink-500" : "border-gray-200 opacity-70 hover:opacity-100"}`}>
                      <img src={u} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Tedarikci fotograflarini (or. 3 renk yan yana) urune dokunmadan temaya uyarla */}
            {selected && (selected.cat.source?.images?.length ?? 0) > 0 && (
              <RestyleProductPhotos catalogId={selected.catalog.id} cat={selected.cat} model={model}
                concepts={shownConcepts.map((c) => ({ url: c.imageUrl, label: c.label }))}
                shotUrls={results.map((r) => r.imageUrl)}
                onDone={() => { void fetchResults(selected.catalog.id, selected.cat.id).then((rows) => { if (rows) setResults(rows); }); }} />
            )}

            {/* Konsept sirasi + basina ek talimat. Sira listedeki gorsel sirasidir,
                ilk konsept ANA gorsel olur; kayitli sette de bu sira saklanir. */}
            {slots.length > 0 && (
              <p className="mb-1 text-[10px] text-gray-400">
                Sıra = listedeki görsel sırası. ★ ile ana görseli seç; “Seçimi kaydet” sırayı da saklar.
              </p>
            )}
            {slots.map((s, i) => {
              const c = concepts.find((x) => x.id === s.mockupId);
              return (
                <div key={s.mockupId} className="mb-2 flex items-start gap-1.5">
                  <span className={`mt-2.5 w-4 shrink-0 text-center text-[11px] font-bold ${i === 0 ? "text-amber-600" : "text-gray-400"}`}>{i + 1}</span>
                  <div className="relative shrink-0">
                    <img src={abs(c?.thumbnailUrl || c?.imageUrl)} alt="" className={`h-10 w-10 rounded object-cover ${i === 0 ? "ring-2 ring-amber-400" : ""}`} />
                    {/* Bu sahneye ozel urun fotografi (ilham penceresinden eslendi) */}
                    {s.productRef && s.productRef !== productImage && (
                      <img src={s.productRef} alt="" title="Bu sahnede kullanılacak ürün fotoğrafı"
                        className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded border border-white object-cover shadow" />
                    )}
                    {i === 0 && <span className="absolute -bottom-1 left-0 right-0 mx-auto w-fit rounded bg-amber-500 px-1 text-[8px] font-bold leading-tight text-white">ANA</span>}
                  </div>
                  <div className="flex shrink-0 flex-col">
                    <button onClick={() => moveSlot(i, -1)} disabled={i === 0} title="Yukarı taşı"
                      className="rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-25">
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => moveSlot(i, 1)} disabled={i === slots.length - 1} title="Aşağı taşı"
                      className="rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-25">
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button onClick={() => makeMain(i)} disabled={i === 0}
                    title={i === 0 ? "Ana görsel" : "Ana görsel yap (sıra 1'e alınır)"}
                    className={`mt-2 shrink-0 ${i === 0 ? "text-amber-500" : "text-gray-300 hover:text-amber-500"}`}>
                    <Star className={`h-3.5 w-3.5 ${i === 0 ? "fill-amber-400" : ""}`} />
                  </button>
                  <select
                    value={s.metal ?? ""}
                    onChange={(e) => setSlots((prev) => prev.map((x, j) => (j === i ? { ...x, metal: e.target.value as Metal } : x)))}
                    title="Metal rengi"
                    className="mt-0.5 shrink-0 rounded border border-gray-200 px-1 py-1 text-[11px]"
                  >
                    <option value="">Renk: aynen</option>
                    {(Object.keys(METAL_LABELS) as Array<Exclude<Metal, "">>).map((k) => <option key={k} value={k}>{METAL_LABELS[k]}</option>)}
                  </select>
                  <input
                    value={s.extraPrompt}
                    onChange={(e) => setSlots((prev) => prev.map((x, j) => (j === i ? { ...x, extraPrompt: e.target.value } : x)))}
                    placeholder="Ek talimat (opsiyonel)"
                    className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]"
                  />
                </div>
              );
            })}

            {/* Isim/harf kolyesi: gorselde yazacak degerler. Katalogtaki ornekler
                gelir, uretim oncesi degistirilebilir. */}
            {perso?.enabled && (
              <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-2">
                <p className="mb-1 text-[11px] font-medium text-indigo-800">
                  Kişiselleştirme ({perso.slots} alan){perso.font ? ` · ${perso.font}` : ""}
                </p>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: perso.slots }).map((_, i) => (
                    <input
                      key={i}
                      value={persoValues[i] ?? ""}
                      onChange={(e) => setPersoValues((prev) => {
                        const next = [...prev];
                        next[i] = e.target.value;
                        return next;
                      })}
                      placeholder={`İsim ${i + 1}`}
                      className="w-24 rounded border border-gray-200 px-1.5 py-1 text-[11px]"
                    />
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-gray-500">
                  Bu değerler görselde yazacak şekilde üretime iletilir; yazı tipi ürün fotoğrafındaki stile uydurulur.
                </p>
              </div>
            )}

            {/* Model secimi: kalite/maliyet dengesi kullaniciya birakilir */}
            {models.length > 0 && (
              <label className="mt-3 block text-[11px] font-medium text-gray-600">
                Model
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
                >
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{m.price ? ` · ${m.price}` : ""}
                    </option>
                  ))}
                </select>
                {(() => {
                  const sel = models.find((m) => m.id === model);
                  return sel?.description
                    ? <span className="mt-0.5 block text-[10px] font-normal text-gray-400">{sel.description}</span>
                    : null;
                })()}
              </label>
            )}

            {/* Kayitli ayarlar: ture gore otomatik + adlandirilmis */}
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 text-[11px]">
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-600">Ayarlar</span>
                <select value={presetId}
                  onChange={(e) => {
                    const id = e.target.value; setPresetId(id);
                    const p = prefs.presets.find((x) => x.id === id);
                    if (p) applySettings(p.s);
                  }}
                  className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-1 py-0.5">
                  <option value="">{prefs.presets.length ? "Kayıtlı ayar seç…" : "Kayıtlı ayar yok"}</option>
                  {prefs.presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button
                  onClick={() => {
                    const name = prompt("Ayar adı", presetId ? prefs.presets.find((x) => x.id === presetId)?.name : `${productType ?? "genel"} ayarı`);
                    if (!name?.trim()) return;
                    const existing = prefs.presets.find((x) => x.name === name.trim());
                    const id = existing?.id ?? `p_${Date.now()}`;
                    const presets = existing
                      ? prefs.presets.map((x) => (x.id === id ? { ...x, s: currentSettings() } : x))
                      : [...prefs.presets, { id, name: name.trim(), s: currentSettings() }];
                    persistPrefs({ ...prefs, presets });
                    setPresetId(id);
                  }}
                  title="Manken, model ve prompt ayarlarını isimle kaydet (aynı isim varsa güncellenir)"
                  className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-gray-700 hover:bg-gray-100">Kaydet</button>
                {presetId && (
                  <button onClick={() => { persistPrefs({ ...prefs, presets: prefs.presets.filter((x) => x.id !== presetId) }); setPresetId(""); }}
                    title="Kayıtlı ayarı sil" className="rounded px-1 text-gray-400 hover:text-red-600">×</button>
                )}
              </div>
              {productType && (
                <p className="mt-1 text-gray-400">
                  {JEWELRY_TYPE_LABELS[productType] ?? productType} ürünleri için son ayarlar otomatik hatırlanır.
                </p>
              )}
            </div>

            {/* Manken gorunumu — secili konseptlerin analizine gore */}
            {(() => {
              const sel = slots.map((sl) => concepts.find((c) => c.id === sl.mockupId)).filter(Boolean) as Array<{ label?: string; conceptAnalysis?: Record<string, unknown> | null }>;
              const withModel = sel.filter((c) => scenePerson(c.conceptAnalysis) === true);
              const parts = new Set<string>(withModel.flatMap((c) => (Array.isArray(c.conceptAnalysis?.visibleParts) ? (c.conceptAnalysis!.visibleParts as string[]) : ["face", "hair", "hand", "ear", "neck"])).map((x) => String(x).toLowerCase()));
              // Mankenli sahne secili degilse (henuz konsept yok / analiz belirsiz)
              // tum alanlar gosterilir: secim onceden yapilip hatirlanabilsin.
              const has = (...p: string[]) => withModel.length === 0 || p.some((x) => parts.has(x));
              const field = (key: keyof typeof LOOK_OPTIONS, label: string) => (
                <label key={key} className="flex items-center justify-between gap-1">
                  <span className="text-gray-500">{label}</span>
                  <select value={(look[key] as string) ?? ""} onChange={(e) => setLook((l) => ({ ...l, [key]: e.target.value || undefined }))}
                    className="w-40 rounded border border-gray-200 px-1 py-0.5">
                    <option value="">değiştirme</option>
                    {LOOK_OPTIONS[key].map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </label>
              );
              return (
                <div className="mt-3 rounded-lg border border-gray-200 p-2 text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-600">Manken</span>
                    <select value={look.mode} onChange={(e) => setLook((l) => ({ ...l, mode: e.target.value as ModelLookState["mode"] }))}
                      className="rounded border border-gray-200 px-1 py-0.5">
                      <option value="keep">Referanstaki gibi kalsın</option>
                      <option value="auto">Otomatik farklı (her görselde)</option>
                      <option value="custom">Özel</option>
                    </select>
                  </div>
                  {sel.length > 0 && withModel.length === 0 && (
                    <p className="mt-1 text-gray-400">Seçili sahnelerde manken yok (ya da analizde belirsiz) — bu ayar uygulanmaz. Emin değilseniz konsepti yeniden analiz edin.</p>
                  )}
                  {sel.length > withModel.length && withModel.length > 0 && (
                    <p className="mt-1 text-gray-400">{sel.length - withModel.length} sahnede manken yok; onlara uygulanmaz.</p>
                  )}
                  {look.mode === "auto" && withModel.length > 0 && (
                    <p className="mt-1 text-gray-400">Ten, saç, yaş, oje, makyaj her görselde rastgele seçilir (yalnız sahnede görünen kısımlar). Poz, kadraj, ışık aynen kalır.</p>
                  )}
                  {look.mode === "custom" && (
                    <div className="mt-1.5 space-y-1">
                      {has("face", "ear", "neck", "hand", "wrist", "finger", "skin") && field("skinTone", "Ten rengi")}
                      {has("hair", "face") && field("hairColor", "Saç rengi")}
                      {has("hair", "face") && field("hairTexture", "Saç tipi")}
                      {has("face", "hand") && field("age", "Yaş")}
                      {has("hand", "finger") && field("nails", "Oje")}
                      {has("face") && field("makeup", "Makyaj")}
                      <p className="text-gray-400">
                        {withModel.length > 0
                          ? "Yalnızca seçili sahnelerde görünen kısımlar listelenir."
                          : "Seçimler hatırlanır; yalnızca mankenli sahnelerde uygulanır."}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            <label className="mt-3 block text-[11px] font-medium text-gray-600">
              Yalnızca prompt ile üret
              <textarea
                value={promptOnly}
                onChange={(e) => setPromptOnly(e.target.value)}
                rows={2}
                placeholder="Konsept seçmeden, sahneyi metinle tarif et"
                className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
              />
            </label>

            <button
              onClick={generate}
              disabled={busy || slots.length === 0 || !productImage}
              title={!productImage ? "Ürünün fotoğrafı yok" : "Seçili konseptlerde ürünü göster"}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-pink-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-pink-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Görsel Üret ({slots.length})
            </button>
            {/* Toplu uretim: sunucu kuyrugu. Panel kapansa da surer. */}
            <button
              onClick={enqueueBatch}
              disabled={busy || slots.length === 0 || !selected}
              title="Seçili ürünleri konseptlerle sunucuda üret"
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              Kuyruğa al ({1 + batchIds.length} ürün x {slots.length} konsept)
            </button>

            {job && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-700">
                <div className="flex items-center justify-between">
                  <span>
                    Kuyruk: {job.doneCount}/{job.total} üretildi
                    {job.failedCount > 0 ? ` · ${job.failedCount} hata` : ""}
                  </span>
                  <span className="text-gray-400">{job.status}</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-200">
                  <div className="h-full bg-emerald-500" style={{ width: `${job.total ? (job.doneCount / job.total) * 100 : 0}%` }} />
                </div>
                {job.failedCount > 0 && ["done", "error"].includes(job.status) && (
                  <button
                    onClick={async () => {
                      await fetch(`${getBackendUrl()}/api/shot-jobs/${job.id}/retry-failed`, { method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ model: model || undefined }) });
                      setJob({ ...job, status: "processing" });
                    }}
                    className="mt-1 text-[11px] text-indigo-600 underline"
                  >
                    Hatalıları yeniden dene
                  </button>
                )}
                {/* Kuyruk bitti: sonraki adim (4 · Urunler / 5 · Liste Denetimi) */}
                {job.status === "done" && job.doneCount > 0 && (
                  <div className="mt-2 flex gap-2">
                    <Link to="/jewelry-products"
                      className="flex flex-1 items-center justify-center gap-1 rounded border border-gray-200 bg-white py-1 font-medium text-gray-700 hover:bg-gray-50">
                      Ürünler'e git
                    </Link>
                    <button
                      onClick={() => navigate(`/listing-review/jewelry?products=${encodeURIComponent(jobProducts.join(","))}`)}
                      disabled={jobProducts.length === 0}
                      className="flex flex-1 items-center justify-center gap-1 rounded bg-fuchsia-600 py-1 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
                    >
                      Denetime gönder <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {log && <p className="mt-1 text-[11px] text-gray-600">{log}</p>}
            {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}

            {results.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-[11px] font-medium text-gray-700">Bu ürünün görselleri ({results.length})</p>
                  <button
                    onClick={() => void (async () => {
                      if (!selected) return;
                      setDownloading("0/" + results.length);
                      try {
                        const r = await downloadImagesToFolder(results.map((x) => abs(x.imageUrl)), {
                          baseName: selected.cat.name,
                          subfolder: selected.cat.name,
                          onProgress: (d, t) => setDownloading(`${d}/${t}`),
                        });
                        if (r) setLog(r.mode === "folder" ? `${r.saved} görsel klasöre kaydedildi` : `${r.saved} görsel indirildi`);
                      } finally { setDownloading(null); }
                    })()}
                    disabled={downloading !== null}
                    title={folderPickerSupported()
                      ? "Klasör seç: görseller ürün adıyla bir alt klasöre sıralı adlarla kaydedilir"
                      : "Bu tarayıcı klasör seçmeyi desteklemiyor — görseller tek tek indirilir (Chrome/Edge önerilir)"}
                    className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {downloading ? <><Loader2 className="h-3 w-3 animate-spin" /> {downloading}</> : <><Download className="h-3 w-3" /> Klasöre indir</>}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {results.map((shot, i) => (
                    <div key={shot.id ?? shot.imageUrl} className="group relative">
                      <img
                        src={abs(shot.imageUrl)}
                        alt=""
                        onClick={() => setLightbox({
                          urls: results.map((x) => abs(x.imageUrl)), index: i, deletable: true,
                          captions: results.map((x) => ({
                            text: [x.concept?.label ? `Konsept: ${x.concept.label}` : "Konsept yok (prompt)", lookText(x.modelLook) ? `Manken: ${lookText(x.modelLook)}` : "Manken: referanstaki gibi", x.model ? `Model: ${x.model}` : ""].filter(Boolean).join(" · "),
                            // Yaninda: kullanilan konsept sahnesi ve urun referansi
                            refs: [
                              ...((x.sceneUrl || x.concept?.imageUrl) ? [{ url: abs(x.sceneUrl || x.concept!.imageUrl), label: `Konsept referansı${x.concept?.label ? ` — ${x.concept.label}` : ""}` }] : []),
                              ...(x.productRefUrl ? [{ url: abs(x.productRefUrl), label: "Ürün referansı" }] : []),
                            ],
                          })),
                        })}
                        className="h-20 w-20 cursor-zoom-in rounded border border-gray-200 object-cover"
                      />
                      {/* Begenilen cikti konseptin ORNEGI olur: sonraki uretimlerde
                          modele referans verilir, sonuclar tutarli kalir */}
                      {slots.length === 1 && (
                        <button
                          onClick={() => void addSampleOutput(slots[0].mockupId, shot.imageUrl)}
                          title="Bu sonucu konseptin örneği yap"
                          className="absolute right-0.5 top-0.5 rounded bg-white/85 p-0.5 text-amber-600 hover:bg-white"
                        >
                          <Star className="h-3 w-3" />
                        </button>
                      )}
                      {/* Kaynak: hangi konseptten, hangi manken ayariyla */}
                      {shot.concept && (
                        <img src={abs(shot.concept.thumbnailUrl || shot.concept.imageUrl)} alt=""
                          title={`Konsept: ${shot.concept.label}${lookText(shot.modelLook) ? `\nManken: ${lookText(shot.modelLook)}` : ""}${shot.model ? `\nModel: ${shot.model}` : ""}`}
                          className="absolute bottom-0.5 left-0.5 h-6 w-6 rounded border border-white object-cover shadow" />
                      )}
                      {shot.isPrimary && (
                        <span title="Listenin ana görseli"
                          className="absolute bottom-0.5 right-0.5 rounded bg-amber-500 px-1 text-[8px] font-bold text-white group-hover:hidden">ANA</span>
                      )}
                      {shot.modelLook && (
                        <span title={`Manken: ${lookText(shot.modelLook)}`}
                          className="absolute left-0.5 top-0.5 rounded bg-fuchsia-600/90 px-1 text-[8px] font-bold text-white">M</span>
                      )}
                      {/* Tek cumlelik duzeltme: yeni kare yanina eklenir */}
                      {shot.id && (
                        <button
                          onClick={() => void reviseShot(i)}
                          disabled={revising !== null}
                          title="Bu görseli tek cümleyle düzelt"
                          className="absolute right-0.5 top-6 hidden rounded bg-white/85 p-0.5 text-indigo-600 hover:bg-white group-hover:block disabled:opacity-50"
                        >
                          {revising === shot.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        </button>
                      )}
                      {/* Begenilen kare konsept kutuphanesine: baska urunlerde sahne olur */}
                      <button
                        onClick={() => void addShotAsConcept(shot.imageUrl)}
                        disabled={conceptSaving === shot.imageUrl || savedAsConcept.has(shot.imageUrl)}
                        title={savedAsConcept.has(shot.imageUrl) ? "Konsept kütüphanesinde" : "Konsept kütüphanesine ekle"}
                        aria-label="Konsept kütüphanesine ekle"
                        className={`absolute right-0.5 top-11 rounded bg-white/85 p-0.5 hover:bg-white disabled:opacity-80 ${savedAsConcept.has(shot.imageUrl) ? "block text-emerald-600" : "hidden text-indigo-600 group-hover:block"}`}
                      >
                        {conceptSaving === shot.imageUrl ? <Loader2 className="h-3 w-3 animate-spin" /> : savedAsConcept.has(shot.imageUrl) ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      </button>
                      {/* Hatali cikan kare: burada da, tam ekranda da silinebilir */}
                      <button
                        onClick={() => void deleteShot(i)}
                        title="Görseli sil"
                        className="absolute bottom-0.5 right-0.5 hidden rounded bg-white/85 p-0.5 text-red-500 hover:bg-white group-hover:block"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Uretilen gorseller listeye tasinir — Katalog → Studio →
                    Listing Builder zinciri (Trendyol akisinin aynisi) */}
                <button
                  onClick={toListingBuilder}
                  className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Liste oluştur <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <p className="mt-2 flex items-start gap-1.5 text-[10px] text-gray-400">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              Sahne korunur (manken, poz, ışık, kadraj); yalnızca takı katalogdaki
              ürünle değiştirilir.
            </p>
          </section>
        </div>
      )}

      {preview && (() => {
        const W = 340, H = 340, PAD = 16;
        const right = preview.x + PAD;
        const left = right + W > window.innerWidth ? preview.x - W - PAD : right;
        const top = Math.min(Math.max(PAD, preview.y - H / 2), Math.max(PAD, window.innerHeight - H - PAD));
        return (
          <div
            className="pointer-events-none fixed z-50 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
            style={{ left, top, width: W, height: H }}
          >
            <img src={preview.url} alt="" className="h-full w-full object-contain" />
          </div>
        );
      })()}

      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          captions={lightbox.captions}
          onDelete={lightbox.deletable ? (i) => void deleteShot(i) : undefined}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
        />
      )}

      {openConceptId && (
        <ConceptDetailModal
          concept={concepts.find((c) => c.id === openConceptId)!}
          onClose={() => setOpenConceptId(null)}
          onUpdated={(updated) => setConcepts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
        />
      )}
    </div>
  );
}

// ---- Konsept detayi: bir kereye mahsus AI cozumlemesi + referanslar ----
// Cozumleme sahneyi tarif eder (poz, kadraj, isik, takinin yeri) ve uretim
// prompt'unu besler. Referanslar uc turde: onayli cikti, kaynak liste gorseli,
// urun fotografi. Onayli ciktilar uretimde modele ornek olarak verilir.
type ConceptRef = { id: string; kind: string; imageUrl: string; note?: string | null };
const REF_LABEL: Record<string, string> = {
  sample_output: "Onaylı çıktı",
  source_listing: "Kaynak liste",
  product_ref: "Ürün fotoğrafı",
};
const ANALYSIS_ROWS = ["summary", "productType", "placement", "pose", "framing", "lighting", "background", "styling", "risks"];

function ConceptDetailModal({ concept, onClose, onUpdated }: {
  concept: CustomMockup;
  onClose: () => void;
  onUpdated: (c: CustomMockup) => void;
}) {
  const [refs, setRefs] = useState<ConceptRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newRefUrl, setNewRefUrl] = useState("");
  const [newRefKind, setNewRefKind] = useState("product_ref");
  // Analize ek istek (yalnizca o calistirma icin)
  const [analyzeExtra, setAnalyzeExtra] = useState("");
  // Konsept ve referans gorselleri tam ekran: seri halinde gezilir
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  // Kaynak liste adresi — alandan cikilinca kaydedilir
  const [srcUrl, setSrcUrl] = useState(
    (concept as unknown as { sourceUrl?: string | null }).sourceUrl ?? ""
  );

  async function saveSourceUrl() {
    const next = srcUrl.trim();
    if (next === (((concept as unknown as { sourceUrl?: string | null }).sourceUrl) ?? "")) return;
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/${concept.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ sourceUrl: next }),
      });
      const b = await r.json().catch(() => ({}));
      if (r.ok && b.data) onUpdated(b.data as CustomMockup);
    } catch { /* link kaydedilemedi — kritik degil */ }
  }

  const analysis = (concept as unknown as { conceptAnalysis?: Record<string, unknown> | null }).conceptAnalysis ?? null;

  async function loadRefs() {
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/${concept.id}/references`, { headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (r.ok) setRefs((b.data ?? []) as ConceptRef[]);
    } catch { /* referanslar alinamadi */ }
  }
  useEffect(() => { void loadRefs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [concept.id]);

  async function analyze() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/${concept.id}/analyze-concept`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ extraPrompt: analyzeExtra.trim() || undefined }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || b.error || "Analiz edilemedi");
      onUpdated(b.data as CustomMockup);
    } catch (e) { setErr(e instanceof Error ? e.message : "Analiz edilemedi"); }
    finally { setBusy(false); }
  }

  async function addRef() {
    if (!newRefUrl.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/${concept.id}/references`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ kind: newRefKind, imageUrl: newRefUrl.trim() }),
      });
      if (!r.ok) throw new Error("Referans eklenemedi");
      setNewRefUrl("");
      await loadRefs();
    } catch (e) { setErr(e instanceof Error ? e.message : "Referans eklenemedi"); }
    finally { setBusy(false); }
  }

  async function removeRef(id: string) {
    setBusy(true);
    try {
      await fetch(`${getBackendUrl()}/api/mockups/references/${id}`, { method: "DELETE", headers: authHeaders() });
      await loadRefs();
    } finally { setBusy(false); }
  }

  const rows = analysis
    ? ANALYSIS_ROWS.map((k) => [k, analysis[k]] as const).filter(([, v]) => typeof v === "string" && v)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{concept.label || "Konsept"}</h3>
            <p className="text-[11px] text-gray-500">
              Sahne çözümlemesi ve referanslar · {concept.productTypeKey ? `ürün tipi: ${concept.productTypeKey}` : "ürün tipi belirsiz"}
            </p>
            {/* Kaynak urun: sahne hangi listeden alindi. Elle de girilebilir,
                cunku eski konseptlerde link yok. */}
            <div className="mt-1 flex items-center gap-1.5">
              <input
                value={srcUrl}
                onChange={(e) => setSrcUrl(e.target.value)}
                onBlur={saveSourceUrl}
                placeholder="Kaynak ürün linki"
                className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]"
              />
              {srcUrl.trim() && (
                <a href={srcUrl} target="_blank" rel="noreferrer"
                  className="flex shrink-0 items-center gap-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-indigo-600 hover:bg-indigo-50">
                  <ExternalLink className="h-3 w-3" /> Aç
                </a>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        {err && <p className="mb-2 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">{err}</p>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <img src={abs(concept.imageUrl)} alt=""
            onClick={() => setLightbox({ urls: [abs(concept.imageUrl), ...refs.map((r) => abs(r.imageUrl))], index: 0 })}
            className="w-full cursor-zoom-in rounded-lg border border-gray-200 object-cover" />
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700">Çözümleme</p>
              <button onClick={analyze} disabled={busy}
                className="flex items-center gap-1 rounded-lg bg-pink-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-pink-700 disabled:opacity-50">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {analysis ? "Yeniden analiz et" : "Analiz et"}
              </button>
            </div>
            <input
              value={analyzeExtra}
              onChange={(e) => setAnalyzeExtra(e.target.value)}
              placeholder="Analize ek istek (ops.): örn. takının ölçeğini vurgula"
              className="mb-2 w-full rounded border border-gray-200 px-2 py-1 text-[11px]"
            />
            {rows.length === 0 ? (
              <p className="rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                Bu konsept henüz çözümlenmedi. Analiz sahneyi (poz, kadraj, ışık, takının yeri) bir kez çıkarır
                ve sonraki tüm üretimlerde kullanılır.
              </p>
            ) : (
              <dl className="space-y-1 text-[11px]">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex gap-1.5">
                    <dt className="w-20 shrink-0 font-medium text-gray-500">{k}</dt>
                    <dd className="min-w-0 flex-1 text-gray-800">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1 text-xs font-semibold text-gray-700">Referanslar ({refs.length})</p>
          <div className="mb-2 flex flex-wrap gap-2">
            {refs.map((r, i) => (
              <div key={r.id} className="relative">
                <img src={abs(r.imageUrl)} alt=""
                  onClick={() => setLightbox({ urls: [abs(concept.imageUrl), ...refs.map((x) => abs(x.imageUrl))], index: i + 1 })}
                  className="h-20 w-20 cursor-zoom-in rounded border border-gray-200 object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 text-center text-[9px] text-white">
                  {REF_LABEL[r.kind] ?? r.kind}
                </span>
                <button onClick={() => removeRef(r.id)} disabled={busy}
                  className="absolute right-0.5 top-0.5 rounded bg-white/85 p-0.5 text-red-500 hover:bg-white disabled:opacity-50">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {refs.length === 0 && <p className="text-[11px] text-gray-400">Henüz referans yok.</p>}
          </div>
          <div className="flex gap-1.5">
            <select value={newRefKind} onChange={(e) => setNewRefKind(e.target.value)}
              className="rounded border border-gray-200 px-1.5 py-1 text-[11px]">
              <option value="product_ref">Ürün fotoğrafı</option>
              <option value="sample_output">Onaylı çıktı</option>
              <option value="source_listing">Kaynak liste</option>
            </select>
            <input value={newRefUrl} onChange={(e) => setNewRefUrl(e.target.value)}
              placeholder="Görsel adresi (URL)"
              className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]" />
            <button onClick={addRef} disabled={busy || !newRefUrl.trim()}
              className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              Ekle
            </button>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">
            Onaylı çıktı olarak eklenen görseller üretimde modele örnek verilir; aynı konseptten çıkan
            sonuclar birbirine benzer olur.
          </p>
        </div>
      </div>

      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          captions={lightbox.urls.map((_, i) => ({
            text: i === 0 ? concept.label : REF_LABEL[refs[i - 1]?.kind] ?? refs[i - 1]?.kind,
            url: i === 0 ? srcUrl || null : null,
          }))}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
        />
      )}
    </div>
  );
}
